import { X } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import type { ProgramStep, TrackFieldInfo } from '@/api/actions'
import { Button } from '@/components/ui/button'

import { describeStep, fxWord } from '../describe'
import { RawJsonField } from './ClausesEditor'
import { useEditor } from './context'
import { asList, asObj, blankStep, clone, deleteIn, insertAt, moveItem, removeAt, setIn, setOrDelete, type Obj } from './doc'
import { AddButton, CheckLine, ComboInput, EditField, EventRefInput, IssueLines, ItemCard, ListControls, nameNormalize, NumberInput, RefInput, SELECT_CLASS, SelectInput, TextInput, type Option } from './fields'
import { useListKeys } from './listKeys'
import { FxList } from './ListsEditors'

/**
 * "Step by step" (§11.4): an ordered program; in each step every participant that does something
 * has a track, and the tracks run together. A track is built from the fields the interpreter
 * understands (`meta.track_fields`: play a move, turn, go somewhere, be held, fly through the air,
 * react…) — only the ones in use are shown, the rest are one pick away.
 */

const words = (s: string): string => s.replace(/_/g, ' ')

export const PHASE_LABEL: Record<string, string> = { anticipate: 'Get ready', action: 'Do it', settle: 'Settle down', react: 'React' }

export const TRACK_LABEL: Record<string, string> = {
  clip: 'Plays a move',
  face: 'Turns to',
  loops: 'Times to play the move',
  move: 'Goes somewhere',
  layer: 'Upper body only',
  mask: 'Body parts the move uses',
  attach: 'Is held by / put on',
  detach: 'Is let go',
  toss: 'Flies through the air',
  mount: 'Climbs on to ride',
  dismount: 'Gets off',
  vanish: 'Disappears',
  appear: 'Appears',
  react: 'Reacts',
  sync: 'Times its move to',
  spawn: 'Makes new things appear',
}

const STOP_LABEL: Record<string, string> = {
  beside: 'next to it',
  at: 'right at it',
  behind: 'behind it',
  on: 'on top of it',
  inside: 'inside it',
  edge: 'at its edge',
  across: 'into it and across',
}

const THEN_LABEL: Record<string, string> = { catch: 'is caught', land: 'lands', vanish: 'disappears' }

/** Help written with `code` in backticks (the server's): the code parts in a code face. */
function HelpText({ text }: { text: string }) {
  return (
    <>
      {text.split('`').map((part, i) =>
        i % 2 === 1 ? (
          <code key={i} className="rounded bg-paper-2 px-1 font-mono text-[0.7rem] text-ink-2">
            {part}
          </code>
        ) : (
          part
        ),
      )}
    </>
  )
}

const plainHelp = (text: string): string => text.replace(/`/g, '')

/** Our plain words first, then the server's label, then the name itself. */
const trackLabel = (f: TrackFieldInfo): string => TRACK_LABEL[f.name] ?? f.label ?? words(f.name)

/* -------------------------------------------------------------------------- */
/* Steps                                                                       */
/* -------------------------------------------------------------------------- */

export function StepsEditor({ path, steps, onChange, atLeastOne = false }: { path: string; steps: unknown[]; onChange(next: unknown[]): void; atLeastOne?: boolean }) {
  const { roles } = useEditor()
  const [keys, keyOps] = useListKeys(steps.length)
  return (
    <div className="flex flex-col gap-3">
      {steps.length === 0 ? <p className="text-sm text-ink-2">No steps yet.</p> : null}
      {steps.map((raw, i) => (
        <StepCard
          key={keys[i]}
          path={`${path}.${i}`}
          step={asObj(raw)}
          index={i}
          count={steps.length}
          onChange={(next) => onChange(steps.map((x, j) => (j === i ? next : x)))}
          onMove={(by) => {
            keyOps.move(i, by)
            onChange(moveItem(steps, i, by))
          }}
          onDuplicate={() => {
            keyOps.insert(i + 1)
            onChange(insertAt(steps, i + 1, clone(raw)))
          }}
          onRemove={() => {
            keyOps.remove(i)
            onChange(removeAt(steps, i))
          }}
          removeDisabled={atLeastOne && steps.length <= 1}
        />
      ))}
      <AddButton onClick={() => onChange([...steps, blankStep(roles[0])])} testId="add-step">
        Add a step
      </AddButton>
    </div>
  )
}

function StepCard({
  path,
  step,
  index,
  count,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
  removeDisabled,
}: {
  path: string
  step: Obj
  index: number
  count: number
  onChange(next: Obj): void
  onMove(by: -1 | 1): void
  onDuplicate(): void
  onRemove(): void
  removeDisabled: boolean
}) {
  const { roles, meta } = useEditor()
  const tracks = asObj(step.tracks)
  const inStep = Object.keys(tracks)
  const order = [...roles.filter((r) => inStep.includes(r)), ...inStep.filter((r) => !roles.includes(r))]
  const free = roles.filter((r) => !inStep.includes(r))
  const reading = describeStep(step as ProgramStep, index)
  const addId = useId()
  return (
    <ItemCard
      path={path}
      testId="step"
      title={
        <span className="flex flex-col gap-0.5">
          <span>
            Step {index + 1} · {reading.title}
          </span>
          <span className="text-xs font-normal text-ink-2" data-testid="step-reading">
            {reading.lines.join('. ')}.
          </span>
        </span>
      }
      controls={<ListControls name={`step ${index + 1}`} index={index} count={count} onMove={onMove} onDuplicate={onDuplicate} onRemove={onRemove} removeDisabled={removeDisabled} />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <EditField path={`${path}.phase`} label="Part of the action">
          {(ctrl) => <SelectInput ctrl={ctrl} value={typeof step.phase === 'string' ? step.phase : 'action'} options={meta.phases.map((p) => ({ value: p, label: PHASE_LABEL[p] ?? words(p) }))} onChange={(v) => onChange(setOrDelete(step, ['phase'], v))} />}
        </EditField>
        <DurationField path={`${path}.dur`} value={step.dur} onChange={(v) => onChange(setOrDelete(step, ['dur'], v))} />
        <EditField path={`${path}.wait`} label="Pause after (seconds)">
          {(ctrl) => <NumberInput ctrl={ctrl} value={step.wait} min={0} max={30} step={0.1} placeholder="0" onChange={(v) => onChange(setOrDelete(step, ['wait'], v))} />}
        </EditField>
      </div>

      <TracksBlock path={`${path}.tracks`}>
        {order.map((role) => (
          <TrackCard
            key={role}
            path={`${path}.tracks.${role}`}
            role={role}
            track={asObj(tracks[role])}
            onChange={(next) => onChange(setIn(step, ['tracks', role], next))}
            onRemove={() => onChange(deleteIn(step, ['tracks', role]))}
          />
        ))}
        {free.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={addId} className="text-sm text-ink-2">
              Who else does something in this step?
            </label>
            <select id={addId} className={`${SELECT_CLASS} w-auto`} value="" onChange={(e) => e.target.value && onChange(setIn(step, ['tracks', e.target.value], {}))}>
              <option value="">Choose…</option>
              {free.map((r) => (
                <option key={r} value={r}>
                  The {words(r)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </TracksBlock>

      <div className="flex flex-col gap-2" data-field-path={`${path}.fx`}>
        <p className="text-sm font-medium text-ink">Effects in this step</p>
        <FxList path={`${path}.fx`} items={asList(step.fx)} onChange={(next) => onChange(setOrDelete(step, ['fx'], next))} />
      </div>
    </ItemCard>
  )
}

function TracksBlock({ path, children }: { path: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3" data-field-path={path}>
      <p className="text-sm font-medium text-ink">Who does what</p>
      <IssueLines path={path} />
      {children}
    </div>
  )
}

const DUR_OPTIONS: Option[] = [
  { value: 'clip', label: 'As long as its moves' },
  { value: 'auto', label: 'As long as it needs' },
  { value: 'seconds', label: 'A number of seconds' },
]

function DurationField({ path, value, onChange }: { path: string; value: unknown; onChange(v: unknown): void }) {
  const mode = typeof value === 'number' ? 'seconds' : value === 'auto' ? 'auto' : 'clip'
  return (
    <EditField path={path} label="How long">
      {(ctrl) => (
        <div className="flex gap-2">
          <SelectInput ctrl={ctrl} value={mode} options={DUR_OPTIONS} onChange={(v) => onChange(v === 'seconds' ? 1 : (v ?? 'clip'))} />
          {mode === 'seconds' ? (
            <NumberInput ctrl={{ id: `${ctrl.id}-s`, invalid: ctrl.invalid, describedBy: ctrl.describedBy }} value={value} min={0.1} max={30} step={0.1} className="w-20" onChange={(v) => onChange(v ?? 1)} />
          ) : null}
        </div>
      )}
    </EditField>
  )
}

/* -------------------------------------------------------------------------- */
/* Tracks                                                                      */
/* -------------------------------------------------------------------------- */

/** Worth showing: a server copy carries every field (`null`, `false`, `loops: 1`). */
function shown(name: string, v: unknown): boolean {
  if (v === undefined || v === null || v === false) return false
  if (name === 'loops') return v !== 1
  return true
}

/** A sensible first value when a field is added (`undefined` = just show the empty box). */
function firstValue(name: string, role: string, others: string[]): unknown {
  const other = others[0]
  switch (name) {
    case 'face':
      return other ? `$${other}` : 'camera'
    case 'loops':
      return 2
    case 'layer':
      return 'upper'
    case 'move':
      return { to: other ? `$${other}` : 'spot:middle', stop: 'beside' }
    case 'attach':
      return { to: `$${other ?? role}`, socket: 'hand' }
    case 'detach':
      return { fall: true }
    case 'toss':
      return other
        ? { from: `$${other}.socket.hand`, to: others[1] ? `$${others[1]}.socket.hand` : 'spot:middle', apex: 0.35, then: others[1] ? 'catch' : 'land' }
        : { from: `$${role}`, to: 'spot:middle', apex: 0.35, then: 'land' }
    case 'mount':
      return { on: `$${other ?? role}` }
    case 'dismount':
    case 'appear':
      return {}
    case 'vanish':
      return true
    case 'react':
      return { fx: 'sparkle' }
    case 'sync':
      return { event: 'contact', with: `${other ?? role}:arrive` }
    case 'spawn':
      return { noun: 'apple', count: 1, then: 'fall' }
    default:
      return undefined
  }
}

function TrackCard({ path, role, track, onChange, onRemove }: { path: string; role: string; track: Obj; onChange(next: Obj): void; onRemove(): void }) {
  const { meta, roles } = useEditor()
  // Fields picked or edited here stay on screen even while empty.
  const [kept, setKept] = useState<string[]>([])
  const known = meta.track_fields.map((f) => f.name)
  const visible = known.filter((n) => shown(n, track[n]) || kept.includes(n))
  const extra = Object.keys(track).filter((k) => !known.includes(k) && shown(k, track[k]))
  const addable = meta.track_fields.filter((f) => !visible.includes(f.name))
  const common = addable.filter((f) => !f.advanced)
  const more = addable.filter((f) => f.advanced)
  const others = roles.filter((r) => r !== role)
  const keep = (name: string): void => setKept((k) => (k.includes(name) ? k : [...k, name]))
  const addId = useId()
  const setField = (name: string, v: unknown): void => {
    keep(name)
    onChange(v === undefined ? deleteIn(track, [name]) : setIn(track, [name], v))
  }
  return (
    <ItemCard
      path={path}
      testId="track"
      className="bg-paper-2/60"
      title={<span data-role={role}>The {words(role)}</span>}
      controls={
        <Button variant="ghost" size="sm" onClick={onRemove} aria-label={`The ${words(role)} does nothing in this step`}>
          <X aria-hidden />
          Nothing
        </Button>
      }
    >
      {visible.length === 0 && extra.length === 0 ? <p className="text-xs text-ink-3">Does nothing yet. Pick what the {words(role)} does:</p> : null}
      {visible.map((name) => (
        <TrackField
          key={name}
          path={`${path}.${name}`}
          info={meta.track_fields.find((f) => f.name === name)!}
          role={role}
          value={track[name]}
          onChange={(v) => setField(name, v)}
          onRemove={() => {
            setKept((k) => k.filter((x) => x !== name))
            onChange(deleteIn(track, [name]))
          }}
        />
      ))}
      {extra.map((name) => (
        <RawJsonField key={name} path={`${path}.${name}`} label={`${words(name)} (as JSON)`} value={track[name]} onChange={(v) => onChange(setIn(track, [name], v))} />
      ))}
      {addable.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={addId} className="sr-only">
            Add to what the {words(role)} does
          </label>
          <select
            id={addId}
            className={`${SELECT_CLASS} w-auto`}
            value=""
            data-testid="track-add"
            onChange={(e) => {
              const name = e.target.value
              if (!name) return
              keep(name)
              const first = firstValue(name, role, others)
              if (first !== undefined) onChange(setIn(track, [name], first))
            }}
          >
            <option value="">+ The {words(role)} also…</option>
            {common.map((f) => (
              <option key={f.name} value={f.name} title={plainHelp(f.help)}>
                {trackLabel(f)}
              </option>
            ))}
            {more.length > 0 ? (
              <optgroup label="More">
                {more.map((f) => (
                  <option key={f.name} value={f.name} title={plainHelp(f.help)}>
                    {trackLabel(f)}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </div>
      ) : null}
    </ItemCard>
  )
}

function FieldFrame({ path, label, help, onRemove, showErrors, children }: { path: string; label: string; help?: string; onRemove(): void; showErrors: boolean; children: ReactNode }) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-paper p-3" data-field-path={path} data-testid="track-field" data-field={path.split('.').pop()}>
      <legend className="sr-only">{label}</legend>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink" aria-hidden>
          {label}
          {help ? (
            <span className="block text-xs font-normal text-ink-3">
              <HelpText text={help} />
            </span>
          ) : null}
        </p>
        <Button variant="ghost" size="icon" className="size-7 shrink-0 [&_svg]:size-4" aria-label={`Remove “${label}”`} onClick={onRemove}>
          <X aria-hidden />
        </Button>
      </div>
      {showErrors ? <IssueLines path={path} /> : null}
      {children}
    </fieldset>
  )
}

function TrackField({ path, info, role, value, onChange, onRemove }: { path: string; info: TrackFieldInfo; role: string; value: unknown; onChange(v: unknown): void; onRemove(): void }) {
  const { meta, roles, lists } = useEditor()
  const clips = lists?.clips
  const name = info.name
  const label = trackLabel(info)
  const obj = asObj(value)
  const sub = (key: string, v: unknown): void => onChange(setOrDelete(obj, [key], v))
  const roleOpts = roles.map((r) => ({ value: `$${r}`, label: `The ${words(r)}` }))
  // Object fields show their own problems on the frame; one-value fields on their control.
  const whole = ['move', 'attach', 'detach', 'toss', 'mount', 'dismount', 'appear', 'react', 'sync', 'spawn'].includes(name)
  const frame = (children: ReactNode): ReactNode => (
    <FieldFrame path={path} label={label} help={info.help} onRemove={onRemove} showErrors={whole}>
      {children}
    </FieldFrame>
  )
  const grid = 'grid gap-2 sm:grid-cols-2'
  switch (name) {
    case 'clip':
      return frame(
        <EditField path={path} label="Move" hideLabel>
          {(ctrl) => <ComboInput ctrl={ctrl} value={value} suggestions={meta.clip_names} listId={clips} normalize={nameNormalize} placeholder="wave, jump, throw…" onChange={onChange} />}
        </EditField>,
      )
    case 'face':
      return frame(
        <EditField path={path} label="Turns to" hideLabel>
          {(ctrl) => <RefInput ctrl={ctrl} value={value} faces onChange={onChange} />}
        </EditField>,
      )
    case 'loops':
      return frame(
        <EditField path={path} label="Times" hideLabel>
          {(ctrl) => <NumberInput ctrl={ctrl} value={value} min={1} max={20} step={1} className="w-24" onChange={onChange} />}
        </EditField>,
      )
    case 'layer':
      // One layer ("upper") is a switch; should the server ever offer more, a pick-one list.
      return frame(
        meta.layers.length === 1 ? (
          <CheckLine path={path} label="Only the arms and head move (the legs keep going)" checked={typeof value === 'string' && value !== ''} onChange={(v) => onChange(v ? meta.layers[0] : undefined)} />
        ) : (
          <EditField path={path} label="Body" hideLabel>
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof value === 'string' ? value : undefined} empty="Whole body" options={meta.layers.map((l) => ({ value: l, label: l === 'upper' ? 'Upper body only (legs keep going)' : words(l) }))} onChange={onChange} />}
          </EditField>
        ),
      )
    case 'mask':
      return frame(
        <EditField path={path} label="Body parts" hideLabel>
          {(ctrl) => <ComboInput ctrl={ctrl} value={value} suggestions={meta.masks} normalize={nameNormalize} placeholder="upper, head, hold…" onChange={onChange} />}
        </EditField>,
      )
    case 'move':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.to`} label="To">
            {(ctrl) => <RefInput ctrl={ctrl} value={obj.to} onChange={(v) => sub('to', v)} />}
          </EditField>
          <EditField path={`${path}.stop`} label="Stops">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.stop === 'string' ? obj.stop : 'beside'} options={meta.stops.map((s) => ({ value: s, label: STOP_LABEL[s] ?? words(s) }))} onChange={(v) => sub('stop', v)} />}
          </EditField>
          <EditField path={`${path}.clip`} label="How it goes" hint="Empty = its usual walk, fly or swim.">
            {(ctrl) => <ComboInput ctrl={ctrl} value={obj.clip === 'move' ? undefined : obj.clip} suggestions={meta.clip_names} listId={clips} normalize={nameNormalize} placeholder="move" onChange={(v) => sub('clip', v)} />}
          </EditField>
          <EditField path={`${path}.speed`} label="Speed" hint="Optional.">
            {(ctrl) => <NumberInput ctrl={ctrl} value={obj.speed} min={0} step={0.5} onChange={(v) => sub('speed', v)} />}
          </EditField>
        </div>,
      )
    case 'attach':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.to`} label="Who holds it">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.to === 'string' ? obj.to : undefined} options={roleOpts} onChange={(v) => sub('to', v)} />}
          </EditField>
          <EditField path={`${path}.socket`} label="Where">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.socket === 'string' ? obj.socket : 'hand'} options={meta.sockets.map((s) => ({ value: s, label: words(s) }))} onChange={(v) => sub('socket', v)} />}
          </EditField>
        </div>,
      )
    case 'detach':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.at`} label="Lands at" hint="Optional: where it ends up.">
            {(ctrl) => <RefInput ctrl={ctrl} value={obj.at} onChange={(v) => sub('at', v)} />}
          </EditField>
          <CheckLine path={`${path}.fall`} label="Falls to the ground" checked={obj.fall === true} onChange={(v) => onChange(setIn(obj, ['fall'], v))} />
        </div>,
      )
    case 'toss':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.from`} label="From">
            {(ctrl) => <RefInput ctrl={ctrl} value={obj.from} onChange={(v) => sub('from', v)} />}
          </EditField>
          <EditField path={`${path}.to`} label="To">
            {(ctrl) => <RefInput ctrl={ctrl} value={obj.to} onChange={(v) => sub('to', v)} />}
          </EditField>
          <EditField path={`${path}.then`} label="Then it">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.then === 'string' ? obj.then : 'land'} options={meta.toss_then.map((t) => ({ value: t, label: THEN_LABEL[t] ?? words(t) }))} onChange={(v) => sub('then', v)} />}
          </EditField>
          <EditField path={`${path}.apex`} label="How high (0–1)">
            {(ctrl) => <NumberInput ctrl={ctrl} value={obj.apex} min={0} max={1} step={0.05} placeholder="0.35" onChange={(v) => sub('apex', v)} />}
          </EditField>
          <EditField path={`${path}.at_event`} label="Starts when" hint="Optional, e.g. thrower:release.">
            {(ctrl) => <EventRefInput ctrl={ctrl} value={obj.at_event} onChange={(v) => sub('at_event', v)} />}
          </EditField>
          <EditField path={`${path}.dur`} label="Flight time (s)" hint="Optional.">
            {(ctrl) => <NumberInput ctrl={ctrl} value={obj.dur} min={0.1} max={10} step={0.1} onChange={(v) => sub('dur', v)} />}
          </EditField>
        </div>,
      )
    case 'mount':
      return frame(
        <EditField path={`${path}.on`} label="Rides on">
          {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.on === 'string' ? obj.on : undefined} options={roleOpts.filter((o) => o.value !== `$${role}`)} onChange={(v) => sub('on', v)} />}
        </EditField>,
      )
    case 'dismount':
    case 'appear':
      return frame(
        <EditField path={`${path}.at`} label={name === 'appear' ? 'Where' : 'Gets off at'} hint="Optional.">
          {(ctrl) => <RefInput ctrl={ctrl} value={obj.at} onChange={(v) => onChange(setOrDelete(obj, ['at'], v))} />}
        </EditField>,
      )
    case 'vanish':
      return frame(<CheckLine path={path} label="Disappears in this step" checked={value === true} onChange={(v) => onChange(v ? true : undefined)} />)
    case 'react':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.clip`} label="Plays" hint="shake, sway, open, bob…">
            {(ctrl) => <ComboInput ctrl={ctrl} value={obj.clip} suggestions={meta.clip_names} listId={clips} normalize={nameNormalize} onChange={(v) => sub('clip', v)} />}
          </EditField>
          <EditField path={`${path}.fx`} label="Effect">
            {(ctrl) => <SelectInput ctrl={ctrl} value={typeof obj.fx === 'string' ? obj.fx : undefined} empty="No effect" options={meta.fx_kinds.map((k) => ({ value: k, label: fxWord(k) }))} onChange={(v) => sub('fx', v)} />}
          </EditField>
        </div>,
      )
    case 'sync':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.event`} label="Its moment">
            {(ctrl) => <ComboInput ctrl={ctrl} value={obj.event} suggestions={meta.moments} normalize={nameNormalize} placeholder="contact" onChange={(v) => sub('event', v)} />}
          </EditField>
          <EditField path={`${path}.with`} label="Meets">
            {(ctrl) => <EventRefInput ctrl={ctrl} value={obj.with} onChange={(v) => sub('with', v)} />}
          </EditField>
        </div>,
      )
    case 'spawn':
      return frame(
        <div className={grid}>
          <EditField path={`${path}.noun`} label="What appears" hint="A thing's name: apple, flower…">
            {(ctrl) => <TextInput ctrl={ctrl} value={obj.noun} onChange={(v) => sub('noun', v.trim() ? v : undefined)} />}
          </EditField>
          <EditField path={`${path}.count`} label="How many (1–5)">
            {(ctrl) => <NumberInput ctrl={ctrl} value={obj.count} min={1} max={5} step={1} onChange={(v) => sub('count', v)} />}
          </EditField>
          <EditField path={`${path}.at`} label="Where" hint="Optional: the top of it by default.">
            {(ctrl) => <RefInput ctrl={ctrl} value={obj.at} onChange={(v) => sub('at', v)} />}
          </EditField>
          <EditField path={`${path}.then`} label="Then">
            {(ctrl) => (
              <SelectInput
                ctrl={ctrl}
                value={typeof obj.then === 'string' ? obj.then : 'fall'}
                options={[
                  { value: 'fall', label: 'falls to the ground' },
                  { value: 'appear', label: 'just stays there' },
                ]}
                onChange={(v) => sub('then', v)}
              />
            )}
          </EditField>
        </div>,
      )
    default:
      return frame(<GenericField path={path} info={info} value={value} onChange={onChange} />)
  }
}

/** A field the form has no special editor for, by the type the server says it has. */
function GenericField({ path, info, value, onChange }: { path: string; info: TrackFieldInfo; value: unknown; onChange(v: unknown): void }) {
  const { meta, lists } = useEditor()
  switch (info.type) {
    case 'clip':
      return <EditField path={path} label={words(info.name)} hideLabel>{(ctrl) => <ComboInput ctrl={ctrl} value={value} suggestions={meta.clip_names} listId={lists?.clips} normalize={nameNormalize} onChange={onChange} />}</EditField>
    case 'ref':
      return <EditField path={path} label={words(info.name)} hideLabel>{(ctrl) => <RefInput ctrl={ctrl} value={value} onChange={onChange} />}</EditField>
    case 'number':
      return <EditField path={path} label={words(info.name)} hideLabel>{(ctrl) => <NumberInput ctrl={ctrl} value={value} onChange={onChange} />}</EditField>
    case 'bool':
      return <CheckLine path={path} label={words(info.name)} checked={value === true} onChange={(v) => onChange(v ? true : undefined)} />
    default:
      return <RawJsonField path={path} label={`${words(info.name)} (as JSON)`} value={value ?? {}} onChange={onChange} />
  }
}
